import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ChevronRight, ChevronLeft, BookOpen, Building2, BarChart3, Link2, CreditCard, CheckCircle2, Radar } from "lucide-react";
import { getPromoConfig, freeMonthsOf } from "@/lib/promo";
import { useSeo } from "@/lib/seo";

const BASE_TUTORIALS = [
    {
        id: "dashboard-agenzia",
        title: "Come funziona la Dashboard Agenzia",
        duration: "3 min",
        icon: Building2,
        steps: [
            { title: "Crea il tuo account agenzia", body: "Registrati con la tua email professionale. Scegli il piano 'Agenzia' durante l'onboarding per sbloccare annunci illimitati, branding personalizzato e statistiche avanzate.", visual: "account" },
            { title: "Personalizza il branding", body: "Carica il logo, scrivi una descrizione della tua agenzia, aggiungi il banner di copertina. Tutto questo apparirà sui tuoi annunci e nella tua pagina pubblica agenzia.", visual: "branding" },
            { title: "Pubblica i primi annunci", body: "Vai su 'Pubblica annuncio'. Compila tutti i campi richiesti: foto (consigliato min. 5), descrizione dettagliata, prezzo, indirizzo. Più dettagli = più contatti.", visual: "publish" },
            { title: "Gestisci la chat e i contatti", body: "Quando un acquirente richiede il contatto, ricevi un'email con un pulsante 'Accetta'. Un click e si apre la conversazione. Tu hai sempre il controllo dei tuoi dati.", visual: "chat" },
            { title: "Monitora le performance", body: "Dalla Dashboard vedi: annunci attivi, views, contatti ricevuti, conversion rate. Cliccando su 'VD Manager' (se attivo) accedi alla dashboard avanzata.", visual: "stats" },
        ],
    },
    {
        id: "analytics-pro",
        title: "Come usare VD Manager",
        duration: "4 min",
        icon: BarChart3,
        steps: [
            { title: "Attiva VD Manager", body: "VD Manager è incluso nel piano Agenzia + VD Manager (€129/mese). Attiva il piano dalla pagina Abbonamenti: sblocchi CRM, Acquisizione Privati, AI Assistant e molto altro. Cancella quando vuoi, senza vincoli.", visual: "subscribe" },
            { title: "Esplora la KPI Dashboard", body: "Vedi i 5 KPI principali: annunci attivi, views totali, media views per annuncio, contatti ricevuti, conversion rate. Tutti i dati sono aggiornati in tempo reale.", visual: "kpi" },
            { title: "Analizza l'heatmap performance", body: "Ogni tuo annuncio è rappresentato come una barra colorata: oro = top performer, viola = buono, blu = medio, grigio = sotto la media. Identifica subito su cosa puntare.", visual: "heatmap" },
            { title: "Imposta soglie di alert", body: "Configura le soglie personalizzate: ricevi una notifica se le views scendono sotto N, se non arrivano contatti, o se un annuncio è inattivo da troppi giorni.", visual: "alerts" },
            { title: "Distribuisci sui portali esterni", body: "Attiva la distribuzione multi-portale: i tuoi annunci vengono pubblicati automaticamente anche su Idealista, Casa.it e Immobiliare.it tramite feed XML privato.", visual: "feeds" },
        ],
    },
    {
        id: "api-portali",
        title: "Come collegare i portali (Idealista, Casa.it, Immobiliare.it)",
        duration: "5 min",
        icon: Link2,
        steps: [
            { title: "Prerequisito: account portale attivo", body: "Devi già avere un account 'Pro' o 'Business' attivo su ciascuno dei portali con cui vuoi sincronizzare. Senza account portale, il feed non viene importato.", visual: "prereq" },
            { title: "Vai su VD Manager → Distribuzione", body: "Apri la sezione 'Distribuzione multi-portale' nella tua dashboard VD Manager. Vedrai le card dei portali: Idealista, Casa.it, Immobiliare.it.", visual: "feeds-tab" },
            { title: "Attiva il portale desiderato", body: "Clicca 'Attiva Idealista' (o Casa.it, o Immobiliare.it). Il sistema genera per te un URL XML privato che contiene i tuoi annunci attivi.", visual: "enable" },
            { title: "Copia l'URL feed", body: "Clicca '📋 Copia URL'. L'URL è privato (contiene un token unico per la tua agenzia): proteggilo come una password.", visual: "copy" },
            { title: "Configura il portale", body: "Vai sul tuo account Idealista/Casa.it/Immobiliare.it, sezione 'Importazione XML' o 'Feed esterno'. Incolla l'URL. Salva. Il portale importerà automaticamente i tuoi annunci ogni 24h.", visual: "configure" },
            { title: "Verifica la sincronizzazione", body: "Su VD Manager vedi il contatore 'hits' del feed: ogni volta che il portale lo importa, il numero sale. Se passano 48h senza hit, controlla la configurazione lato portale.", visual: "verify" },
        ],
    },
    {
        id: "nuovi-privati",
        title: "Come funziona Nuovi Privati (acquisizione automatica)",
        duration: "4 min",
        icon: Radar,
        steps: [
            { title: "Cos'è la sezione Nuovi Privati", body: "È il tuo scanner automatico di annunci di privati pubblicati sui portali esterni (Subito.it, Bakeca, Kijiji, gruppi Facebook e altri). Il sistema li recupera in tempo reale così sei sempre il primo a chiamare il proprietario prima della concorrenza.", visual: "acquisition-intro" },
            { title: "Come il sistema trova gli annunci", body: "VendiDirect usa un motore di ricerca web (Serper.dev) che interroga il web ogni volta che apri la sezione. Filtra solo annunci di privati (esclude agenzie), applica la categoria immobiliare corretta e ripulisce i risultati fuori zona. Nessun database duplicato: i dati arrivano freschi ad ogni ricerca.", visual: "acquisition-engine" },
            { title: "Match automatico con i clienti del CRM", body: "Ogni nuovo annuncio trovato viene confrontato con la lista di clienti acquirenti del tuo CRM. Se prezzo, zona, mq e tipologia combaciano con almeno un cliente, l'annuncio riceve un badge oro 'Match' e ti indica quale cliente contattare per proporglielo.", visual: "acquisition-match" },
            { title: "Notifiche dei match trovati", body: "I match rilevati compaiono nella card 'Briefing del giorno' in Dashboard e nella sezione Notifiche del CRM. Cliccando sul match apri direttamente la scheda del cliente e dell'annuncio esterno, con il pulsante 'Contatta proprietario' pronto all'uso.", visual: "acquisition-notify" },
            { title: "Filtri per affinare i risultati", body: "Usa i filtri in cima alla lista: Città (Milano, Roma, Torino…), Tipologia (appartamento, villa, box, terreno), Range prezzo, Superficie minima, Data pubblicazione. Salva le tue ricerche preferite come 'Monitor' per riceverle in automatico ogni giorno senza rifare i filtri.", visual: "acquisition-filters" },
            { title: "Gestisci ed esporta i risultati", body: "Da ogni riga puoi: aggiungere l'annuncio ai tuoi lead, marcarlo come 'già contattato' per non rivederlo, esportare la selezione in CSV, o creare direttamente un nuovo cliente venditore. Ogni azione viene registrata nello storico del CRM.", visual: "acquisition-manage" },
        ],
    },
];

function buildAbbonamentiTutorial(cfg) {
    const anyPromo = !!cfg?.any_promo;
    const mBase = freeMonthsOf(cfg, "base");
    const mPremium = freeMonthsOf(cfg, "premium");
    const mAgenzia = freeMonthsOf(cfg, "agenzia");
    const maxMonths = cfg?.max_months || 0;
    const dopoLabel = (m) => (m > 0 ? `Dopo i ${m} mes${m === 1 ? "e" : "i"}` : "Prezzo");
    const steps = [];
    if (anyPromo) {
        steps.push({
            title: `🎁 Promo lancio: fino a ${maxMonths} mes${maxMonths === 1 ? "e" : "i"} GRATIS`,
            body: `Al momento sono disponibili mesi gratis sui piani eleggibili. La durata specifica per ciascun piano è visibile sulla pagina Prezzi. Nessuna carta di credito richiesta. Attivazione immediata.`,
            visual: "promo",
        });
    }
    steps.push({ title: "Piano Base (privati)", body: `Per chi vende/affitta UNA casa. Include: 1 annuncio attivo, foto illimitate, chat con acquirenti su consenso. ${dopoLabel(mBase)}: €9,90/mese.`, visual: "base" });
    steps.push({ title: "Piano Premium (privati)", body: `Per chi ha più immobili. Include: fino a 5 annunci, statistiche base, annuncio in evidenza, supporto prioritario. ${dopoLabel(mPremium)}: €19,90/mese.`, visual: "premium" });
    steps.push({ title: "Piano Agenzia", body: `Pensato per le agenzie immobiliari. Annunci illimitati, branding, pagina agenzia pubblica, statistiche avanzate. ${dopoLabel(mAgenzia)}: €79,90/mese.`, visual: "agency" });
    steps.push({ title: "VD Manager (incluso nel piano Agenzia)", body: "Il piano Agenzia + VD Manager (€129/mese) include tutto: CRM completo, Acquisizione Privati, AI Assistant, campagne email/WhatsApp, dashboard avanzata, distribuzione multi-portale.", visual: "pro" });
    return { id: "abbonamenti", title: "Tutti i piani spiegati", duration: "2 min", icon: CreditCard, steps };
}

export default function TutorialPage() {
    useSeo({ title: "Tutorial: come pubblicare e vendere casa su VendiDirect", description: "Guide passo-passo per privati e agenzie: pubblicazione annunci, VD Manager CRM, feed portali, abbonamenti e Radar Aste.", canonical: "/tutorial" });
    const [promoCfg, setPromoCfg] = useState(null);
    useEffect(() => { getPromoConfig(true).then(setPromoCfg); }, []);
    const TUTORIALS = useMemo(() => [...BASE_TUTORIALS, buildAbbonamentiTutorial(promoCfg)], [promoCfg]);
    const [active, setActive] = useState(BASE_TUTORIALS[0].id);
    const tutorial = TUTORIALS.find((t) => t.id === active);

    return (
        <div data-testid="tutorial-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Sidebar */}
                <aside className="lg:col-span-3">
                    <div className="sticky top-32">
                        <div className="flex items-center gap-2 mb-3"><BookOpen size={14} className="text-gold" /><span className="label-eyebrow">Tutorial</span></div>
                        <h1 className="font-serif text-3xl text-ink-50 mb-10">Impara <span className="italic text-gold">vendidirect</span>.it</h1>
                        <nav className="space-y-2" data-testid="tutorial-nav">
                            {TUTORIALS.map((t) => {
                                const Icon = t.icon;
                                const isActive = t.id === active;
                                return (
                                    <button
                                        key={t.id}
                                        data-testid={`tutorial-nav-${t.id}`}
                                        onClick={() => setActive(t.id)}
                                        className={`w-full text-left p-4 border transition-colors flex items-start gap-3 ${isActive ? "border-gold/50 bg-gold/5" : "border-white/10 hover:border-white/30 bg-ink-900/40"}`}
                                    >
                                        <Icon size={18} className={isActive ? "text-gold" : "text-ink-400"} />
                                        <div className="flex-1">
                                            <div className={`text-sm font-medium ${isActive ? "text-ink-50" : "text-ink-100"}`}>{t.title}</div>
                                            <div className="text-[10px] text-ink-400 mt-1">{t.duration} · {t.steps.length} step</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </aside>

                {/* Player */}
                <main className="lg:col-span-9">
                    <TutorialPlayer key={active} tutorial={tutorial} />
                </main>
            </div>
        </div>
    );
}

function TutorialPlayer({ tutorial }) {
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const timerRef = useRef(null);
    const total = tutorial.steps.length;
    const step = tutorial.steps[idx];
    const STEP_MS = 8000;

    useEffect(() => {
        if (!playing) return;
        const tick = 50;
        timerRef.current = setInterval(() => {
            setProgress((p) => {
                const np = p + (tick / STEP_MS) * 100;
                if (np >= 100) {
                    if (idx + 1 < total) { setIdx(idx + 1); return 0; }
                    setPlaying(false); return 100;
                }
                return np;
            });
        }, tick);
        return () => clearInterval(timerRef.current);
    }, [playing, idx, total]);

    const goPrev = () => { if (idx > 0) { setIdx(idx - 1); setProgress(0); } };
    const goNext = () => { if (idx + 1 < total) { setIdx(idx + 1); setProgress(0); } };
    const togglePlay = () => setPlaying((p) => !p);

    return (
        <div className="space-y-6">
            {/* Stage */}
            <div className="relative aspect-video bg-gradient-to-br from-ink-900 via-ink-950 to-black border border-white/10 overflow-hidden">
                <AnimatedVisual key={`${tutorial.id}-${idx}`} kind={step.visual} stepIdx={idx} />
                <div className="absolute top-6 left-6 flex items-center gap-2 z-10">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80 bg-black/40 px-3 py-1">{tutorial.title}</span>
                </div>
                <div className="absolute top-6 right-6 z-10 text-xs text-ink-400 bg-black/40 px-3 py-1">{idx + 1} / {total}</div>
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black via-black/70 to-transparent z-10">
                    <h2 data-testid="tutorial-step-title" className="font-serif text-3xl sm:text-4xl text-ink-50 leading-tight max-w-3xl">{step.title}</h2>
                    <p className="text-ink-100 mt-4 max-w-2xl text-sm sm:text-base leading-relaxed">{step.body}</p>
                </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4">
                <button data-testid="tutorial-prev" onClick={goPrev} disabled={idx === 0} className="p-3 border border-white/15 hover:border-gold disabled:opacity-30"><ChevronLeft size={18} /></button>
                <button data-testid="tutorial-playpause" onClick={togglePlay} className="cta-gold p-3" aria-label={playing ? "Pausa" : "Play"}>{playing ? <Pause size={18} /> : <Play size={18} />}</button>
                <button data-testid="tutorial-next" onClick={goNext} disabled={idx + 1 >= total} className="p-3 border border-white/15 hover:border-gold disabled:opacity-30"><ChevronRight size={18} /></button>
                <div className="flex-1 h-1 bg-ink-900 overflow-hidden">
                    <div className="h-full bg-gold transition-all" style={{ width: `${(idx / total) * 100 + (progress / 100) * (100 / total)}%` }} />
                </div>
            </div>

            {/* Steps list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-8">
                {tutorial.steps.map((s, i) => (
                    <button
                        key={i}
                        data-testid={`tutorial-step-${i}`}
                        onClick={() => { setIdx(i); setProgress(0); }}
                        className={`text-left p-3 border text-sm flex items-start gap-3 transition-colors ${i === idx ? "border-gold/40 bg-gold/5" : i < idx ? "border-white/10 bg-ink-900/40" : "border-white/5 bg-transparent"}`}
                    >
                        {i < idx ? <CheckCircle2 size={14} className="text-gold mt-1 shrink-0" /> : <span className={`text-xs font-mono mt-1 ${i === idx ? "text-gold" : "text-ink-400"}`}>{String(i + 1).padStart(2, "0")}</span>}
                        <span className={i === idx ? "text-ink-50" : "text-ink-400"}>{s.title}</span>
                    </button>
                ))}
            </div>

            <div className="text-center mt-12 p-8 border border-gold/30 bg-gold/5">
                <p className="text-sm text-ink-400 mb-3">Hai dubbi? Il chatbot AI in basso a destra risponde 24/7.</p>
                <Link to="/prezzi" className="cta-gold px-6 py-3 text-sm inline-block">Vai ai piani →</Link>
            </div>
        </div>
    );
}

// Animated visual placeholders — each "kind" renders a distinct CSS animation
function AnimatedVisual({ kind, stepIdx }) {
    const colors = ["#D4AF6A", "#A78BFA", "#60A5FA", "#34D399"];
    const accent = colors[stepIdx % colors.length];
    // Different animated motifs per kind
    return (
        <div className="absolute inset-0 flex items-center justify-center" data-testid={`visual-${kind}`}>
            {/* Grid background */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `linear-gradient(${accent}33 1px, transparent 1px), linear-gradient(90deg, ${accent}33 1px, transparent 1px)`, backgroundSize: "40px 40px", animation: "tut-grid-move 8s linear infinite" }} />
            {/* Orbiting accent shapes */}
            <div className="relative w-48 h-48 sm:w-72 sm:h-72">
                <div className="absolute inset-0 rounded-full border opacity-30" style={{ borderColor: accent, animation: "tut-spin 12s linear infinite" }} />
                <div className="absolute inset-6 rounded-full border opacity-20" style={{ borderColor: accent, animation: "tut-spin 18s linear infinite reverse" }} />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="font-serif font-bold uppercase tracking-[0.4em] text-[10px] sm:text-xs" style={{ color: accent, opacity: 0.6 }}>{kind}</div>
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full" style={{ background: accent, boxShadow: `0 0 30px ${accent}, 0 0 60px ${accent}`, animation: "tut-pulse 2.4s ease-in-out infinite" }} />
                <div className="absolute bottom-0 right-1/2 translate-x-1/2 translate-y-1/2 w-3 h-3 rounded-full" style={{ background: accent, boxShadow: `0 0 20px ${accent}`, animation: "tut-pulse 2s ease-in-out infinite 0.6s" }} />
            </div>
            <style>{`
                @keyframes tut-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
                @keyframes tut-pulse { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.4); opacity: 0.6 } }
                @keyframes tut-grid-move { from { background-position: 0 0 } to { background-position: 40px 40px } }
            `}</style>
        </div>
    );
}
